import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import FilterDropdown from '../common/FilterDropdown';
import { 
    PhoneIcon, 
    EnvelopeIcon, 
    MapPinIcon,
    ChatBubbleLeftRightIcon,
    PaperAirplaneIcon,
    PaperClipIcon
} from '@heroicons/react/24/outline';

const Contact = () => {
  const { post } = useApi();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    full_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
    email: user ? user.email : '',
    category: 'General Feedback',
    subject: '',
    message: '',
    attachment: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user) {
        setFormData(prev => ({
            ...prev,
            full_name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || prev.full_name,
            email: user.email || prev.email
        }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
        let payload = formData;
        if (formData.attachment && formData.category === 'Technical Support') {
            payload = new FormData();
            payload.append('full_name', formData.full_name);
            payload.append('email', formData.email);
            payload.append('category', formData.category);
            payload.append('subject', formData.subject);
            payload.append('message', formData.message);
            payload.append('attachment', formData.attachment);
        }
        
        await post('/api/enquiries/submit/', payload);
        setSubmitted(true);
        setFormData({
            full_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
            email: user ? user.email : '',
            category: 'General Feedback',
            subject: '',
            message: '',
            attachment: null
        });
    } catch (err) {
        console.error('Submission error:', err);
    } finally {
        setIsSubmitting(false);
    }
  };

  const categories = [
    { name: 'Technical Support', value: 'Technical Support' },
    { name: 'Academic Inquiries', value: 'Academic Inquiries' },
    { name: 'Admissions & Enrollment', value: 'Admissions & Enrollment' },
    { name: 'Billing & Finance', value: 'Billing & Finance' },
    { name: 'General Feedback', value: 'General Feedback' },
    { name: 'Complaint', value: 'Complaint' }
  ];

  const contactInfo = [
    {
      title: 'Visit Us',
      details: ['P.O.BOX 48328', 'Kabarsiran Ave, Nairobi'],
      icon: MapPinIcon,
      color: 'bg-indigo-50 text-indigo-600',
    },
    {
      title: 'Call Us',
      details: ['0733846959', 'Mon-Fri 8:00 AM - 4:30 PM'],
      icon: PhoneIcon,
      color: 'bg-amber-50 text-amber-600',
    },
    {
      title: 'Email Us',
      details: ['info@kiandaschool.ac.ke', 'registrar@kiandaschool.ac.ke'],
      icon: EnvelopeIcon,
      color: 'bg-emerald-50 text-emerald-600',
    }
  ];

  const socialLinks = [
    { name: 'Facebook', url: 'https://www.facebook.com/KiandaSchoolOfficial', icon: 'fab fa-facebook' },
    { name: 'Twitter', url: 'https://twitter.com/KiandaSchool', icon: 'fab fa-twitter' },
    { name: 'Instagram', url: 'https://www.instagram.com/kiandaschool/', icon: 'fab fa-instagram' },
    { name: 'LinkedIn', url: 'https://www.linkedin.com/school/kianda-school-nairobi/', icon: 'fab fa-linkedin' }
  ];

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
        setFormData({ ...formData, attachment: e.target.files[0] });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Premium Header Section */}
      <div className="bg-white border-b border-gray-100 shadow-sm mb-12">
        <div className="max-w-7xl mx-auto px-8 py-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-full mb-6">
                <ChatBubbleLeftRightIcon className="w-4 h-4 text-[#18216D]" />
                <span className="text-[#18216D] text-[10px] font-black uppercase tracking-[0.2em]">Contact Gateway</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-[#18216D] tracking-tight mb-4 uppercase">Get In Touch</h1>
            <p className="text-slate-400 font-bold text-lg max-w-2xl mx-auto">
                Have questions about our academic programs or need support? Our team is here to assist you.
            </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Left Column: Contact Cards */}
          <div className="lg:col-span-5 space-y-6">
            <h2 className="text-xl font-black text-[#18216D] uppercase tracking-widest mb-8 flex items-center gap-3">
                <div className="w-8 h-1 bg-[#FFC425] rounded-full" />
                Connectivity Hub
            </h2>
            
            <div className="grid grid-cols-1 gap-6">
                {contactInfo.map((info, idx) => (
                    <div key={idx} className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-indigo-900/5 transition-all group">
                        <div className="flex items-center gap-6">
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 duration-300 ${info.color}`}>
                                <info.icon className="w-7 h-7" />
                            </div>
                            <div>
                                <h3 className="text-sm font-black text-[#18216D] uppercase tracking-widest mb-1">{info.title}</h3>
                                {info.details.map((line, lidx) => (
                                    <p key={lidx} className="text-slate-500 font-bold text-sm leading-relaxed">{line}</p>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Social Connect */}
            <div className="bg-[#18216D] p-8 rounded-[2.5rem] mt-12 shadow-2xl shadow-indigo-900/20 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                <div className="relative z-10">
                    <h3 className="text-white text-sm font-black uppercase tracking-widest mb-6">Digital Presence</h3>
                    <div className="flex gap-4">
                        {socialLinks.map((social) => (
                            <a 
                                key={social.name} 
                                href={social.url} 
                                className="w-12 h-12 bg-white/10 hover:bg-[#FFC425] hover:text-[#18216D] text-white rounded-xl flex items-center justify-center transition-all duration-300 backdrop-blur-sm"
                            >
                                <i className={`${social.icon} text-lg`}></i>
                            </a>
                        ))}
                    </div>
                </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-[3rem] p-10 md:p-14 shadow-sm border border-slate-100 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-slate-50 rounded-bl-full -mr-32 -mt-32 -z-0"></div>
                
                <div className="relative z-10">
                    <h2 className="text-2xl font-black text-[#18216D] mb-2 uppercase">Send a Message</h2>
                    <p className="text-slate-400 font-bold text-sm mb-10">Fill out the form below and we'll get back to you within 24 hours.</p>

                    {submitted ? (
                        <div className="text-center py-10 animate-in fade-in zoom-in duration-500">
                            <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                <PaperAirplaneIcon className="w-10 h-10" />
                            </div>
                            <h2 className="text-2xl font-black text-[#18216D] mb-4 uppercase">Message Received</h2>
                            <p className="text-slate-500 font-bold mb-8">Thank you for reaching out. Your enquiry has been logged and our team will get back to you shortly.</p>
                            <button 
                                onClick={() => setSubmitted(false)}
                                className="text-[#18216D] font-black text-[10px] uppercase tracking-widest hover:underline"
                            >
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-[#18216D] uppercase tracking-widest ml-1">Full Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="John Doe"
                                    disabled={!!user}
                                    className={`w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-[#18216D] placeholder-slate-300 focus:outline-none focus:ring-4 focus:ring-[#18216D]/5 focus:border-[#18216D] transition-all font-sans ${user ? 'opacity-70 bg-slate-50' : ''}`}
                                    value={formData.full_name}
                                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-[#18216D] uppercase tracking-widest ml-1">Email Address</label>
                                <input
                                    type="email"
                                    required
                                    placeholder="john@example.com"
                                    disabled={!!user}
                                    className={`w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-[#18216D] placeholder-slate-300 focus:outline-none focus:ring-4 focus:ring-[#18216D]/5 focus:border-[#18216D] transition-all font-sans ${user ? 'opacity-70 bg-slate-50' : ''}`}
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-[#18216D] uppercase tracking-widest ml-1">Inquiry Category</label>
                            <FilterDropdown
                                options={categories}
                                value={formData.category}
                                onChange={(val) => setFormData({ ...formData, category: val })}
                                placeholder="Select Category"
                                className="w-full"
                            />
                        </div>

                        {formData.category === 'Technical Support' && (
                            <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                <label className="text-[10px] font-black text-[#18216D] uppercase tracking-widest ml-1 flex items-center gap-2">
                                    <PaperClipIcon className="w-3 h-3" /> Error Screenshot (Optional)
                                </label>
                                <div className="flex items-center justify-center w-full">
                                    <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-32 border-2 border-slate-200 border-dashed rounded-2xl cursor-pointer bg-slate-50 hover:bg-slate-100 hover:border-indigo-300 transition-all">
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <PaperClipIcon className="w-6 h-6 mb-2 text-slate-400" />
                                            <p className="mb-1 text-sm text-slate-500 font-bold"><span className="font-black text-[#18216D]">Click to upload</span> or drag and drop</p>
                                            <p className="text-xs text-slate-400 font-bold">PNG, JPG or GIF (MAX. 5MB)</p>
                                            {formData.attachment && <p className="mt-2 text-xs font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">{formData.attachment.name}</p>}
                                        </div>
                                        <input id="dropzone-file" type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                    </label>
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-[#18216D] uppercase tracking-widest ml-1">Inquiry Subject</label>
                            <input
                                type="text"
                                required
                                placeholder="Academic Inquiry"
                                className="w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-[#18216D] placeholder-slate-300 focus:outline-none focus:ring-4 focus:ring-[#18216D]/5 focus:border-[#18216D] transition-all font-sans"
                                value={formData.subject}
                                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-[#18216D] uppercase tracking-widest ml-1">Detailed Message</label>
                            <textarea
                                rows="5"
                                required
                                placeholder="Tell us how we can help..."
                                className="w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-[#18216D] placeholder-slate-300 focus:outline-none focus:ring-4 focus:ring-[#18216D]/5 focus:border-[#18216D] transition-all font-sans resize-none"
                                value={formData.message}
                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            ></textarea>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full flex items-center justify-center gap-3 bg-[#FFC425] text-[#18216D] py-5 rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] hover:bg-[#18216D] hover:text-white transition-all shadow-xl shadow-amber-900/10 group disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <span className="animate-pulse">Processing...</span>
                            ) : (
                                <>
                                    <PaperAirplaneIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                    Dispatch Communication
                                </>
                            )}
                        </button>
                    </form>
                    )}
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;